import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: 'UV Effects | Handmade Fuzz, Overdrive & Boost Pedals',
      description: 'Boutique guitar pedals built in Colorado with NOS germanium diodes and silicon transistors. Fuzz, overdrive, and boost. Free shipping on all orders.'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: function () {
      return import('../views/AboutView.vue')
    },
    meta: {
      title: 'About UV Effects | Colorado Guitar Pedal Builder',
      description: 'Boutique guitar pedals handcrafted in Colorado with NOS germanium and silicon components. Learn about our approach to building fuzz, overdrive, and boost pedals.'
    }
  },
  {
    path: '/shop',
    name: 'shop',
    component: function () {
      return import('../views/PedalsView.vue')
    },
    meta: {
      title: 'Shop Guitar Pedals | UV Effects',
      description: 'Browse handmade fuzz, overdrive, and boost pedals. NOS germanium diodes and silicon transistors. Singles from $95. Free shipping.'
    }
  },
  {
    path: '/pedals',
    redirect: '/shop'
  },
  {
    path: '/pedals/ion',
    name: 'ion',
    component: function () {
      return import('../views/IonView.vue')
    },
    meta: {
      title: 'ION Fuzz Overdrive Pedal | UV Effects',
      description: 'Dual-channel fuzz and overdrive with NOS BC183 transistors and 1N34A germanium diodes. Cleans up with your volume knob. Retired.'
    }
  },
  {
    path: '/pedals/gluon',
    name: 'gluon',
    component: function () {
      return import('../views/GluonView.vue')
    },
    meta: {
      title: 'GLUON One-Knob Fuzz Pedal | UV Effects',
      description: 'Silicon fuzz with NOS BC183 transistors tuned for vintage germanium response. Bias toggle, cleans up with your volume knob. Handmade in Colorado. $95.'
    }
  },
  {
    path: '/pedals/fermion',
    name: 'fermion',
    component: function () {
      return import('../views/FermionView.vue')
    },
    meta: {
      title: 'FERMION Overdrive Pedal | UV Effects',
      description: 'Bluesbreaker-style overdrive with JRC4580DD op amp and NOS 1N34A germanium clipping diodes. Touch-sensitive, amp-like breakup. Handmade in Colorado. $95.'
    }
  },
  {
    path: '/pedals/muon',
    name: 'muon',
    component: function () {
      return import('../views/MuonView.vue')
    },
    meta: {
      title: 'MUON JFET Boost Pedal | UV Effects',
      description: 'Clean boost with NOS J201 JFET and Dirt toggle for tube-like saturation. Responds dynamically to your playing. Handmade in Colorado. $95.'
    }
  },
  {
    path: '/pedals/tauon',
    name: 'tauon',
    component: function () {
      return import('../views/TauonView.vue')
    },
    meta: {
      title: 'TAUON Overdrive Boost Pedal | UV Effects',
      description: 'Dual-channel overdrive and boost with NOS 1N34A germanium diodes and J201 JFET. Stack both sides for touch-sensitive gain. Retired.'
    }
  },
  {
    path: '/pedals/pion',
    name: 'pion',
    component: function () {
      return import('../views/PionView.vue')
    },
    meta: {
      title: 'PION Fuzz Overdrive Boost Pedal | UV Effects',
      description: 'Fuzz, overdrive, and boost in one enclosure. NOS BC183 transistors, 1N34A germanium diodes, J201 JFET. Seven gain combinations. Handmade. $175.'
    }
  },
  {
    path: '/components',
    name: 'components',
    component: function () {
      return import('../views/ComponentsView.vue')
    },
    meta: {
      title: 'NOS Components | UV Effects Pedals',
      description: 'Learn about the NOS germanium diodes, silicon transistors, JFETs, and op amps inside UV Effects pedals. 1N34A, BC183, J201, JRC4580DD and more.'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: function () {
      return import('../views/ContactView.vue')
    },
    meta: {
      title: 'Contact | UV Effects',
      description: 'Questions about UV Effects pedals, custom builds, or orders? Email daniel@uveffects.com. Based in Colorado. Usually respond within 24 hours.'
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: function () {
      return import('../views/PrivacyView.vue')
    },
    meta: {
      title: 'Privacy Policy | UV Effects',
      description: 'Privacy policy for UV Effects. How we collect, use, and protect your information when you visit our website or make a purchase.'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: function () {
      return import('../views/TermsView.vue')
    },
    meta: {
      title: 'Terms of Service | UV Effects',
      description: 'Terms of service for UV Effects guitar pedals. Purchase terms, warranty information, and conditions of use for uveffects.com.'
    }
  },
  {
    path: '/shipping',
    name: 'shipping',
    component: function () {
      return import('../views/ShippingView.vue')
    },
    meta: {
      title: 'Shipping & Returns | UV Effects',
      description: 'Free US shipping on all UV Effects pedals. Same-day processing, 14-day returns, and a 1-year warranty. International orders ship via Reverb.'
    }
  },
  {
    path: '/tonelab',
    name: 'tonelab',
    component: function () {
      return import('../views/LabView.vue')
    },
    meta: {
      title: 'Tone Lab | UV Effects',
      description: 'Technical articles on guitar pedal circuits, clipping diodes, transistors, and the physics of tone. Deep dives into analog design and components.'
    }
  },
  {
    path: '/lab',
    redirect: '/tonelab'
  },
  {
    path: '/tonelab/clipping-diodes-guide',
    name: 'clipping-diodes-article',
    component: function () {
      return import('../views/lab/ClippingDiodesArticle.vue')
    },
    meta: {
      title: 'Clipping Diodes Guide | Tone Lab | UV Effects',
      description: 'Hard vs soft clipping, germanium vs silicon diodes. Learn how 1N34A, 1N4148, and LED clipping shape the tone of RAT, Tube Screamer, and Klon pedals.',
      ogImage: 'https://uveffects.com/germanium-itt-1n34a.webp'
    }
  },
  {
    path: '/lab/clipping-diodes-guide',
    redirect: '/tonelab/clipping-diodes-guide'
  },
  {
    path: '/tonelab/harmonic-series-guide',
    name: 'harmonic-series-article',
    component: function () {
      return import('../views/lab/HarmonicSeriesArticle.vue')
    },
    meta: {
      title: 'Harmonic Series Guide | Tone Lab | UV Effects',
      description: 'The physics of guitar tone explained. Harmonic series, Fourier analysis, and how clipping creates distortion. Why pedals sound the way they do.',
      ogImage: 'https://uveffects.com/img/guitar-frequency-response-thumb.png'
    }
  },
  {
    path: '/lab/harmonic-series-guide',
    redirect: '/tonelab/harmonic-series-guide'
  },
  {
    path: '/tonelab/five-pillars-of-gain',
    name: 'five-pillars-article',
    component: function () {
      return import('../views/lab/FivePillarsArticle.vue')
    },
    meta: {
      title: 'The Five Pillars of Gain | Tone Lab | UV Effects',
      description: 'Tube, BJT, JFET, MOSFET, and Op-Amp circuits compared. Learn how each amplification method shapes guitar tone through impedance, control, and saturation.'
    }
  },
  {
    path: '/accessories/keychain',
    redirect: '/accessories/clipper/'
  },
  {
    path: '/accessories/clipper',
    name: 'clipper',
    component: function () {
      return import('../views/KeychainView.vue')
    },
    meta: {
      title: 'Germanium Clipper Keychain | UV Effects',
      description: 'Passive clipping circuit with NOS germanium diodes on your keychain. Real harmonic saturation in your pocket. Free with every UV Effects pedal.'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // Ensure consistent trailing slash behavior
  strict: false
})

// Helper function to update or create a meta tag
function updateMetaTag(selector, attribute, value) {
  let element = document.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    if (selector.includes('property=')) {
      element.setAttribute('property', selector.match(/property="([^"]+)"/)[1])
    } else if (selector.includes('name=')) {
      element.setAttribute('name', selector.match(/name="([^"]+)"/)[1])
    }
    document.head.appendChild(element)
  }
  element.setAttribute(attribute, value)
}

// Helper function to update or create a link tag
function updateLinkTag(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    document.head.appendChild(element)
  }
  element.href = href
}

// Update document title, meta tags, canonical URL, and OG tags on route change
router.beforeEach((to, from, next) => {
  const baseUrl = 'https://uveffects.com'
  const defaultTitle = 'UV Effects | Boutique Guitar Pedals'
  const defaultDescription = 'Handmade boutique guitar pedals built in Colorado with NOS germanium and silicon components.'
  const defaultImage = `${baseUrl}/og-image.jpg`
  
  const title = to.meta.title || defaultTitle
  const description = to.meta.description || defaultDescription
  // Ensure trailing slash for all paths except root
  const path = to.path === '/' ? '/' : (to.path.endsWith('/') ? to.path : `${to.path}/`)
  const url = `${baseUrl}${path}`
  const image = to.meta.ogImage || defaultImage
  
  // Update page title
  document.title = title
  
  // Update meta description
  updateMetaTag('meta[name="description"]', 'content', description)
  
  // Update canonical URL
  updateLinkTag('canonical', url)
  
  // Update Open Graph tags
  updateMetaTag('meta[property="og:title"]', 'content', title)
  updateMetaTag('meta[property="og:description"]', 'content', description)
  updateMetaTag('meta[property="og:url"]', 'content', url)
  updateMetaTag('meta[property="og:image"]', 'content', image)
  
  // Update Twitter tags
  updateMetaTag('meta[name="twitter:title"]', 'content', title)
  updateMetaTag('meta[name="twitter:description"]', 'content', description)
  updateMetaTag('meta[name="twitter:url"]', 'content', url)
  updateMetaTag('meta[name="twitter:image"]', 'content', image)
  
  next()
})

export default router
